import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ImageIcon from '@atlaskit/icon/core/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CustomMediaConfig } from '../../../../controllers/use-product-dependencies';
import type { ImageProps } from '../../../layout-options';
import type { SelectedImage, UploadedImage } from '../../unsplash/types';

import { MediaPickerContent } from './content';
import { messages } from './messages';

const styles = cssMap({
	mediaPickerPopupContainer: {
		width: '264px',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

export type MediaPickerSectionProps = {
	analyticsSource: string;
	contentId: string;
	customMediaConfig?: CustomMediaConfig;
	image?: ImageProps;
	isDisabled?: boolean;
	onChangeImage: (image: UploadedImage | SelectedImage) => void;
	onOpenUpdate?: (open: boolean) => void;
	shouldShowDeleteButton?: boolean;
	shouldAllowLinkFromUrl?: boolean;
};

export const MediaPickerSection = ({
	onChangeImage,
	image,
	contentId,
	analyticsSource,
	shouldShowDeleteButton = false,
	onOpenUpdate,
	isDisabled = false,
	customMediaConfig,
	shouldAllowLinkFromUrl = false,
}: MediaPickerSectionProps): React.JSX.Element => {
	const [openPopup, setOpenPopup] = useState(false);
	const { formatMessage } = useIntl();

	const handleSetOpenPopup = (open: boolean) => {
		setOpenPopup(open);
		onOpenUpdate?.(open);
	};

	return (
		<Popup
			isOpen={openPopup}
			onClose={() => handleSetOpenPopup(false)}
			content={() => (
				<Box xcss={styles.mediaPickerPopupContainer}>
					<MediaPickerContent
						elementIdPrefix={`${analyticsSource}-media-picker`}
						sectionLabel={<FormattedMessage {...messages.mediaPickerContentTitle} />}
						imageAltText={image?.altText}
						selectedImageUrl={image?.imageSrc}
						initialUnsplashResults={undefined}
						handleImageSelection={onChangeImage}
						contentId={contentId}
						analyticsSource={analyticsSource}
						helperMessage={<FormattedMessage {...messages.mediaPickerContentHelperMessage} />}
						shouldShowDeleteButton={shouldShowDeleteButton}
						isDisabled={isDisabled}
						customMediaConfig={customMediaConfig}
						shouldAllowLinkFromUrl={shouldAllowLinkFromUrl}
					/>
				</Box>
			)}
			placement="bottom-end"
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					analyticsContext={{ source: `${analyticsSource}MediaPickerButton` }}
					iconBefore={ImageIcon}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					onClick={() => handleSetOpenPopup(!openPopup)}
					aria-label={formatMessage(messages.mediaPickerButtonLabel)}
					isSelected={openPopup}
				>
					{/* Box added as button requires children */}
					<Box />
				</Button>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-truecompany-hub')}
		/>
	);
};
